import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Stack, Tooltip } from "@mui/material";
import { CARD_ERROR_SEVERITIES } from "tng-address-collection-shared/cardStatuses.ts";
import { CardsListItem } from "tng-address-collection-shared/types/apiTypes.ts";
import { cardStatusesOf } from "../columns/cardStatuses.ts";
import {
  cardErrorHintTranslationKeys,
  cardSeverityTranslationKeys,
} from "../hooks/useCardErrorColumns.tsx";
import { useTranslator } from "../i18n/translator.ts";
import { TooltipTrigger } from "./DescribedTooltip.tsx";
import { SharedLink } from "./SharedLink.tsx";

export const StatusCell = ({
  card,
  hiddenFields,
  duplicatedNames,
}: {
  card: CardsListItem;
  hiddenFields: string[];
  duplicatedNames?: ReadonlySet<string>;
}) => {
  const translator = useTranslator();
  const statuses = cardStatusesOf(card, hiddenFields, duplicatedNames);
  if (statuses.length === 0 && !card.shared) return null;
  const statusGroups = (["error", "warning"] as const)
    .map((severity) => ({
      severity,
      statuses: statuses.filter(
        (status) => CARD_ERROR_SEVERITIES[status] === severity,
      ),
    }))
    .filter((group) => group.statuses.length > 0);
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {statusGroups.map(({ severity, statuses: groupStatuses }) => {
        const hints = groupStatuses.map((status) =>
          translator(cardErrorHintTranslationKeys[status]),
        );
        const severityLabel = translator(cardSeverityTranslationKeys[severity]);
        return (
          // The name already carries the hints, so they need no description.
          <Tooltip
            key={severity}
            title={hints.join("\n")}
            arrow
            placement="bottom"
            disableInteractive
            slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
          >
            <TooltipTrigger label={`${severityLabel}: ${hints.join("; ")}`}>
              <WarningAmberIcon color={severity} fontSize="small" />
            </TooltipTrigger>
          </Tooltip>
        );
      })}
      <SharedLink
        shared={card.shared}
        cardId={card.id}
        projectId={card.projectId}
        responsibleProjectId={card.responsibleProjectId}
      />
    </Stack>
  );
};
